import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { norm } from '../utils/normalize'
import { fmtDate } from '../utils/format'
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  EyeIcon
} from '@heroicons/react/24/outline'

const API_BASE_URL = '/api/v1/dev'

interface Allergen {
  id: number
  name: string
}

interface Unit {
  id: number
  name: string
}

interface Ingredient {
  id: number
  name: string
  type: string
  default_unit_id: number
  default_unit: Unit
  allergens: Allergen[]
}

interface Batch {
  id: number
  internal_code: string
  lot_number: string
  quantity_available_g: number
  quantity_total_g: number
  best_before: string | null
  expiry_date: string | null
  status: string
  opened_at: string | null
  days_until_expiry: number | null
  allergens_snapshot: Allergen[]
  supplier: {
    id: number
    trade_name: string
  }
}

interface IngredientSummary {
  ingredient: Ingredient
  total_available_g: number
  batch_count: number
  open_batches: number
  expiring_soon: number
}

export default function InventoryByIngredient() {
  const navigate = useNavigate()
  const [ingredients, setIngredients] = useState<IngredientSummary[]>([])
  const [expandedIngredient, setExpandedIngredient] = useState<number | null>(null)
  const [ingredientBatches, setIngredientBatches] = useState<{ [key: number]: Batch[] }>({})
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    fetchInventorySummary()
  }, [])

  const fetchInventorySummary = async () => {
    try {
      setLoading(true)
      setError('')

      const response = await fetch(`${API_BASE_URL}/inventory/summary`)
      const data = await response.json()

      if (response.ok) {
        setIngredients(Array.isArray(data.data) ? data.data : Object.values(data.data))
      } else {
        setError('Error al cargar el resumen de inventario')
      }
    } catch (error) {
      setError('Error de conexión')
      console.error('Error fetching inventory summary:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchIngredientBatches = async (ingredientId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/inventory/by-ingredient/${ingredientId}`)
      const data = await response.json()

      if (response.ok) {
        setIngredientBatches(prev => ({
          ...prev,
          [ingredientId]: data.data.batches
        }))
      }
    } catch (error) {
      console.error('Error fetching ingredient batches:', error)
    }
  }

  const toggleIngredient = async (ingredientId: number) => {
    if (expandedIngredient === ingredientId) {
      setExpandedIngredient(null)
    } else {
      setExpandedIngredient(ingredientId)
      if (!ingredientBatches[ingredientId]) {
        await fetchIngredientBatches(ingredientId)
      }
    }
  }

  const formatWeight = (grams: number) => {
    if (grams >= 1000) {
      return `${(grams / 1000).toFixed(2)} kg`
    }
    return `${Math.round(grams)} g`
  }

  const getExpiryWarning = (days: number | null) => {
    if (days === null) return null
    if (days < 0) return { color: 'text-red-600', text: 'Expirado' }
    if (days <= 3) return { color: 'text-red-600', text: `${days} días` }
    if (days <= 7) return { color: 'text-orange-600', text: `${days} días` }
    return null
  }

  const filteredIngredients = ingredients.filter(item => {
    const searchLower = searchTerm.toLowerCase()
    return norm(item.ingredient.name).includes(norm(searchLower))
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Inventario por Ingrediente</h1>
        <p className="mt-1 text-sm text-gray-600">
          Vista consolidada del stock disponible agrupado por ingrediente
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">{ingredients.length}</span>
                </div>
              </div>
              <div className="ml-4 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Ingredientes en stock
                </dt>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">
                    {ingredients.reduce((sum, item) => sum + item.batch_count, 0)}
                  </span>
                </div>
              </div>
              <div className="ml-4 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total lotes disponibles
                </dt>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-orange-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">
                    {ingredients.reduce((sum, item) => sum + item.expiring_soon, 0)}
                  </span>
                </div>
              </div>
              <div className="ml-4 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Lotes por caducar
                </dt>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
              placeholder="Buscar ingredientes..."
            />
          </div>
        </div>
      </div>

      {/* Ingredients List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="divide-y divide-gray-200">
          {filteredIngredients.map((item) => (
            <div key={item.ingredient.id}>
              {/* Ingredient Header */}
              <div
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => toggleIngredient(item.ingredient.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h3 className="text-lg font-medium text-gray-900">
                        {item.ingredient.name}
                      </h3>
                      {item.expiring_soon > 0 && (
                        <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                          {item.expiring_soon} por caducar
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span>
                        <span className="font-medium text-gray-900">{formatWeight(item.total_available_g)}</span> disponibles
                      </span>
                      <span>•</span>
                      <span>{item.batch_count} lotes</span>
                      <span>•</span>
                      <span>{item.open_batches} abiertos</span>
                      {item.ingredient.allergens && item.ingredient.allergens.length > 0 && (
                        <>
                          <span>•</span>
                          <span className="flex items-center space-x-1">
                            {item.ingredient.allergens.slice(0, 2).map(allergen => (
                              <span
                                key={allergen.id}
                                className="inline-flex px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full"
                              >
                                {allergen.name}
                              </span>
                            ))}
                            {item.ingredient.allergens.length > 2 && (
                              <span className="text-xs text-gray-500">
                                +{item.ingredient.allergens.length - 2}
                              </span>
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    {expandedIngredient === item.ingredient.id ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Batches */}
              {expandedIngredient === item.ingredient.id && (
                <div className="px-6 pb-4 bg-gray-50">
                  {ingredientBatches[item.ingredient.id] ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Código/Lote
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Proveedor
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Cantidad
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Estado
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Caducidad
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Alérgenos
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Acciones
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {ingredientBatches[item.ingredient.id].map((batch) => {
                            const warning = getExpiryWarning(batch.days_until_expiry)
                            return (
                              <tr key={batch.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm font-mono font-medium text-gray-900">
                                    {batch.internal_code}
                                  </div>
                                  <div className="text-xs text-gray-500 font-mono">
                                    {batch.lot_number}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {batch.supplier.trade_name}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {formatWeight(batch.quantity_available_g)}
                                  </div>
                                  <div className="w-24 bg-gray-200 rounded-full h-1.5 mt-1">
                                    <div
                                      className="bg-blue-600 h-1.5 rounded-full"
                                      style={{
                                        width: `${(batch.quantity_available_g / batch.quantity_total_g) * 100}%`
                                      }}
                                    ></div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      batch.status === 'open'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-blue-100 text-blue-800'
                                    }`}
                                  >
                                    {batch.status === 'open' ? 'Abierto' : 'Sin abrir'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {(batch.best_before || batch.expiry_date) && (
                                    <div>
                                      <div className="text-sm text-gray-600">
                                        {fmtDate(batch.best_before || batch.expiry_date!)}
                                      </div>
                                      {warning && (
                                        <div className={`text-xs ${warning.color} mt-1`}>
                                          {warning.text}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {!batch.best_before && !batch.expiry_date && (
                                    <span className="text-sm text-gray-400">N/A</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap gap-1 max-w-xs">
                                    {batch.allergens_snapshot && batch.allergens_snapshot.length > 0 ? (
                                      batch.allergens_snapshot.slice(0, 3).map((allergen) => (
                                        <span
                                          key={allergen.id}
                                          className="inline-flex px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full"
                                        >
                                          {allergen.name}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-xs text-gray-400">Sin alérgenos</span>
                                    )}
                                    {batch.allergens_snapshot && batch.allergens_snapshot.length > 3 && (
                                      <span className="text-xs text-gray-500">+{batch.allergens_snapshot.length - 3}</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={() => navigate(`/inventory/${batch.id}`)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    <EyeIcon className="h-4 w-4 inline" />
                                  </button>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredIngredients.length === 0 && (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500">No hay ingredientes en inventario</p>
          </div>
        )}
      </div>
    </div>
  )
}
